import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Search, Gift, Percent, Sparkles, Clock, AlertCircle, TrendingDown, CheckCircle2, Loader2, CalendarPlus, BadgeCheck, RotateCcw } from "lucide-react";
import { TempPromoModal } from "./TempPromoModal";
import { PromoExtendModal } from "./PromoExtendModal";
import { PromoLiquidateDialog } from "./PromoLiquidateDialog";
import { getExtendablePromo, graceDeadline, MAX_EXTRA_DAYS } from "@/utils/promoState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || ""; }
};
const isPromoLiquidated = (p) => (p.promotions || []).some((pp) => pp.status === "accepted");
const acceptedPromoOf = (p) => (p.promotions || []).find((pp) => pp.status === "accepted");

/**
 * PromotionsSection — self-contained admin surface for managing discount
 * promotions and per-user discount limits.
 *
 *   Left column: searchable client list (only users with at least one
 *                hechicería/pacto plan). Selecting a user opens the right
 *                column.
 *   Right column: shows the user's offer config (allow_offers, max %/$)
 *                 with an inline editor + their eligible plans as cards.
 *                 Each plan has actions: [Añadir oferta], [Promoción
 *                 temporal] and shows the offer_log summary.
 */
const HECHI_RX = /(hechicer|magia|poder|amarre|endulzam|retorn|limpieza|pacto|trabajo|brujer)/i;

export const PromotionsSection = ({ users, plans, authHeader, refreshAll, onOpenOfferApply }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [promoPlan, setPromoPlan] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null); // {plan, promo}
  const [busy, setBusy] = useState(false);
  const [liquidatingId, setLiquidatingId] = useState(null);

  // Clients who have at least one hechicería-eligible plan
  const eligibleUsers = useMemo(() => {
    const byUser = new Map();
    for (const p of plans || []) {
      const name = ((p.service_name || "") + " " + (p.service_id || "")).toLowerCase();
      if (/lectura/i.test(name)) continue;
      if (!HECHI_RX.test(name)) continue;
      const uid = p.user_id;
      if (!byUser.has(uid)) byUser.set(uid, []);
      byUser.get(uid).push(p);
    }
    const out = [];
    for (const [uid, uPlans] of byUser.entries()) {
      const u = (users || []).find((x) => x.id === uid);
      if (!u) continue;
      out.push({ ...u, hechi_plans: uPlans });
    }
    // Newest activity first
    out.sort((a, b) => {
      const at = Math.max(...a.hechi_plans.map((p) => new Date(p.updated_at || p.created_at).getTime()));
      const bt = Math.max(...b.hechi_plans.map((p) => new Date(p.updated_at || p.created_at).getTime()));
      return bt - at;
    });
    return out;
  }, [users, plans]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligibleUsers;
    return eligibleUsers.filter((u) => (
      (u.name || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.visible_id || "").toLowerCase().includes(q) ||
      (u.phone || "").includes(q)
    ));
  }, [search, eligibleUsers]);

  const selectedUser = useMemo(
    () => eligibleUsers.find((u) => u.id === selectedUserId),
    [eligibleUsers, selectedUserId],
  );

  // Local state mirror for the offer config editor (avoids stale writes)
  const [configDraft, setConfigDraft] = useState({ allow_offers: false, max_offer_pct: "", max_offer_amount: "" });
  const loadConfig = (u) => {
    setConfigDraft({
      allow_offers: !!u.allow_offers,
      max_offer_pct: u.max_offer_pct ?? "",
      max_offer_amount: u.max_offer_amount ?? "",
    });
  };
  const pickUser = (u) => {
    setSelectedUserId(u.id);
    loadConfig(u);
  };

  const [liquidateTarget, setLiquidateTarget] = useState(null); // {plan, promo}
  const liquidateWithPromo = (planId, promoId) => {
    const plan = (selectedUser?.hechi_plans || []).find((p) => p.id === planId);
    const promo = (plan?.promotions || []).find((pp) => pp.id === promoId);
    if (!plan || !promo) return toast.error("Promoción no encontrada");
    setLiquidateTarget({ plan, promo });
  };
  const confirmLiquidate = async (reflected) => {
    const { plan, promo } = liquidateTarget || {};
    if (!plan) return;
    setLiquidatingId(plan.id);
    try {
      await axios.post(
        `${API}/admin/payment-plans/${plan.id}/promotions/${promo.id}/liquidate`,
        { reflected },
        { headers: authHeader() },
      );
      toast.success(reflected ? "¡Plan liquidado con la promoción!" : "Plan liquidado — pago registrado como pendiente de reflejarse");
      setLiquidateTarget(null);
      await refreshAll?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo liquidar");
    } finally {
      setLiquidatingId(null);
    }
  };

  const saveConfig = async () => {
    if (!selectedUser) return;
    setBusy(true);    try {
      const body = {
        allow_offers: !!configDraft.allow_offers,
      };
      // Only send non-empty pct/amount (both are optional in backend)
      if (configDraft.max_offer_pct !== "" && configDraft.max_offer_pct !== null) {
        body.max_offer_pct = Number(configDraft.max_offer_pct);
      } else {
        body.max_offer_pct = null;
      }
      if (configDraft.max_offer_amount !== "" && configDraft.max_offer_amount !== null) {
        body.max_offer_amount = Number(configDraft.max_offer_amount);
      } else {
        body.max_offer_amount = null;
      }
      await axios.patch(`${API}/admin/users/${selectedUser.id}/offers`, body, { headers: authHeader() });
      toast.success("Configuración guardada");
      await refreshAll?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  const fmtMoney = (n, cur = "MXN") => `${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${cur}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4"
      data-testid="promotions-section"
    >
      {/* Left: eligible client list */}
      <div className="bg-gradient-to-br from-black/60 to-purple-900/10 border border-[#d4af37]/25 rounded-2xl p-3">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-[#d4af37]" />
          <div className="font-serif text-sm text-white">Clientes con hechicería</div>
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-neutral-500" />
          <input
            data-testid="promotions-user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, teléfono, ID…"
            className="w-full bg-black/40 border border-white/10 rounded pl-7 pr-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#d4af37]"
          />
        </div>
        <div className="text-[10px] text-neutral-500 mb-2">{filteredUsers.length} cliente(s)</div>
        <div className="space-y-1 max-h-[68vh] overflow-y-auto pr-1">
          {filteredUsers.map((u) => (
            <button
              key={u.id}
              data-testid={`promo-user-${u.id}`}
              onClick={() => pickUser(u)}
              className={`w-full text-left rounded-lg border p-2 text-[11px] transition ${
                selectedUserId === u.id
                  ? "bg-[#d4af37]/15 border-[#d4af37]/60"
                  : "bg-black/30 border-white/5 hover:border-[#d4af37]/30"
              }`}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-white font-semibold truncate">{u.name || u.username}</span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  {u.hechi_plans.some(isPromoLiquidated) && (
                    <span data-testid={`promo-user-liquidated-${u.id}`} className="text-[8px] uppercase tracking-widest text-sky-200 bg-sky-500/20 border border-sky-400/40 px-1.5 py-0.5 rounded-full">Liquidado c/ promo</span>
                  )}
                  {u.hechi_plans.some((p) => (p.promotions || []).some((pp) => pp.status === "active" && (pp.valid_until || "") > new Date().toISOString())) && (
                    <span className="text-[8px] uppercase tracking-widest text-amber-200 bg-amber-500/15 border border-amber-400/40 px-1.5 py-0.5 rounded-full animate-pulse">Promo activa</span>
                  )}
                  {u.allow_offers && (
                    <span className="text-[8px] uppercase tracking-widest text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded-full">Ofertas ✓</span>
                  )}
                </span>
              </div>
              <div className="text-neutral-500 text-[10px] mt-0.5">
                {u.hechi_plans.length} trabajo{u.hechi_plans.length !== 1 ? "s" : ""} · {u.visible_id}
              </div>
            </button>
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-[11px] text-neutral-500 py-6 text-center">
              {search ? "Sin coincidencias" : "Aún no hay clientes con trabajos de hechicería"}
            </div>
          )}
        </div>
      </div>

      {/* Right: user detail + plan cards */}
      <div className="bg-gradient-to-br from-black/60 to-purple-900/10 border border-[#d4af37]/25 rounded-2xl p-4">
        {!selectedUser ? (
          <div className="h-full flex flex-col items-center justify-center py-16 text-center text-neutral-500 text-sm">
            <Gift className="w-8 h-8 text-[#d4af37]/50 mb-2" />
            Selecciona un cliente para configurar sus descuentos y promociones
          </div>
        ) : (
          <>
            {/* Client header */}
            <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-white/5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/70">Cliente</div>
                <div className="font-serif text-lg text-white">{selectedUser.name || selectedUser.username}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {selectedUser.visible_id} · {selectedUser.phone || "sin teléfono"}
                </div>
              </div>
            </div>

            {/* Offer config editor */}
            <div className="rounded-lg bg-black/40 border border-white/10 p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5 text-emerald-300" />
                  <div className="text-[11px] uppercase tracking-widest text-neutral-300 font-semibold">Configuración de descuentos</div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="promo-config-allow"
                    type="checkbox"
                    checked={configDraft.allow_offers}
                    onChange={(e) => setConfigDraft((d) => ({ ...d, allow_offers: e.target.checked }))}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <span className="text-[11px] text-emerald-300 font-semibold">Habilitar</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Máx. % descuento</div>
                  <input
                    data-testid="promo-config-max-pct"
                    type="number"
                    min="0"
                    max="99"
                    disabled={!configDraft.allow_offers}
                    value={configDraft.max_offer_pct}
                    onChange={(e) => setConfigDraft((d) => ({ ...d, max_offer_pct: e.target.value }))}
                    placeholder="30"
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white disabled:opacity-40 focus:outline-none focus:border-emerald-400"
                  />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Máx. monto descuento</div>
                  <input
                    data-testid="promo-config-max-amt"
                    type="number"
                    min="0"
                    disabled={!configDraft.allow_offers}
                    value={configDraft.max_offer_amount}
                    onChange={(e) => setConfigDraft((d) => ({ ...d, max_offer_amount: e.target.value }))}
                    placeholder="2000"
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white disabled:opacity-40 focus:outline-none focus:border-emerald-400"
                  />
                </div>
              </div>
              <div className="text-[10px] text-neutral-500 mb-2 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Los límites aplican <b className="text-neutral-300">acumulativamente</b>: la suma de descuentos aplicados no puede pasar de estos topes.
              </div>
              <button
                data-testid="promo-config-save"
                disabled={busy}
                onClick={saveConfig}
                className="btn-gold text-[10px] px-3 py-1.5 rounded-full uppercase tracking-widest font-bold disabled:opacity-50"
              >
                Guardar configuración
              </button>
            </div>

            {/* Plans list */}
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
              Trabajos de hechicería ({selectedUser.hechi_plans.length})
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {selectedUser.hechi_plans.map((p) => {
                const cur = p.currency || "MXN";
                const agreed = Number(p.agreed_price_after_offer ?? p.agreed_price ?? 0);
                const paid = Number(p.paid_total ?? 0);
                const remaining = Math.max(0, agreed - paid);
                const priorOffers = (p.offer_log || []).filter((o) => !o.promotion_id).reduce((s, o) => s + Number(o.discount_amount || 0), 0);
                const ext = getExtendablePromo(p);
                const activePromo = ext?.isActive ? ext.promo : null;
                const expiredPromo = ext?.isRecentlyExpired ? ext.promo : null;
                const acceptedPromo = acceptedPromoOf(p);
                const isPaid = p.fully_paid || p.status === "paid";
                return (
                  <div
                    key={p.id}
                    data-testid={`promo-plan-${p.id}`}
                    className={`rounded-lg border p-3 text-xs ${acceptedPromo ? "border-sky-400/40 bg-sky-500/[0.06]" : "border-white/10 bg-black/50"}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold truncate">{p.service_name}</div>
                        <div className="text-neutral-500 text-[10px] mt-0.5">
                          Total {fmtMoney(agreed, cur)} · pagado {fmtMoney(paid, cur)} · resta <b className={remaining > 0 ? "text-amber-300" : "text-emerald-300"}>{fmtMoney(remaining, cur)}</b>
                        </div>
                      </div>
                      {isPaid && (
                        <span data-testid={`promo-plan-status-${p.id}`} className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1 ${acceptedPromo ? "text-sky-200 bg-sky-500/20 border border-sky-400/40" : "text-emerald-300 bg-emerald-500/15"}`}>
                          <BadgeCheck className="w-3 h-3" /> {acceptedPromo ? "Liquidado con promo" : "Liquidado"}
                        </span>
                      )}
                    </div>
                      {acceptedPromo && (
                        <div data-testid={`promo-plan-accepted-${p.id}`} className="mb-2 rounded border border-sky-400/30 bg-sky-500/10 px-2 py-1.5 text-[10px] text-sky-100">
                          <div className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-sky-300" /> Liquidado con promoción temporal</div>
                          <div className="text-neutral-300 mt-0.5">
                            Descuento {acceptedPromo.kind === "percentage" ? `${acceptedPromo.value}%` : fmtMoney(acceptedPromo.value, cur)} = <b className="text-emerald-300">−{fmtMoney(acceptedPromo.discount_amount, cur)}</b>
                            {" · "}pagó {fmtMoney(acceptedPromo.new_remaining, cur)} de {fmtMoney(acceptedPromo.remaining_before, cur)}
                          </div>
                          <div className="text-neutral-500 mt-0.5">
                            {fmtDate(acceptedPromo.accepted_at)}
                            {Number(acceptedPromo.extended_days_total || 0) > 0 && ` · con prórroga de +${acceptedPromo.extended_days_total} día(s)`}
                          </div>
                        </div>
                      )}
                      {(priorOffers > 0 || activePromo || expiredPromo) && (
                        <div className="mb-2 flex flex-wrap gap-1.5 text-[10px]">
                          {priorOffers > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                              <TrendingDown className="w-3 h-3" /> Ofertas: {fmtMoney(priorOffers, cur)}
                            </span>
                          )}
                          {activePromo && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 animate-pulse">
                              <Clock className="w-3 h-3" /> Promo activa · descuento {fmtMoney(activePromo.discount_amount, cur)} · vence {fmtDate(activePromo.valid_until)}
                              {Number(activePromo.extended_days_total || 0) > 0 && ` · +${activePromo.extended_days_total}d`}
                            </span>
                          )}
                          {expiredPromo && !isPaid && (
                            <span data-testid={`promo-expired-grace-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/40 text-rose-200">
                              <AlertCircle className="w-3 h-3" /> Promo vencida {fmtDate(expiredPromo.valid_until)} · reactivable hasta {fmtDate(graceDeadline(expiredPromo).toISOString())}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selectedUser.allow_offers && !isPaid && (
                          <button
                            data-testid={`promo-add-offer-${p.id}`}
                            onClick={() => onOpenOfferApply?.(p)}
                            className="text-[10px] px-2.5 py-1 rounded border border-emerald-500/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1 font-semibold"
                            title="Aplicar un descuento fijo permanente sobre el precio"
                          >
                            <Percent className="w-3 h-3" /> Añadir oferta
                          </button>
                        )}
                        {!isPaid && (
                          <button
                            data-testid={`promo-add-promo-${p.id}`}
                            onClick={() => setPromoPlan(p)}
                            className="text-[10px] px-2.5 py-1 rounded border border-rose-500/60 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 flex items-center gap-1 font-semibold"
                            title="Crear promoción temporal — se envía imagen al cliente con validez limitada"
                          >
                            <Sparkles className="w-3 h-3" /> {activePromo ? "Ver promoción" : "Promoción temporal"}
                          </button>
                        )}
                        {activePromo && !isPaid && (
                          <button
                            data-testid={`promo-extend-${p.id}`}
                            disabled={Number(activePromo.extended_days_total || 0) >= MAX_EXTRA_DAYS}
                            onClick={() => setExtendTarget({ plan: p, promo: activePromo })}
                            className="text-[10px] px-2.5 py-1 rounded border border-sky-400/60 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 flex items-center gap-1 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            title={Number(activePromo.extended_days_total || 0) >= MAX_EXTRA_DAYS ? "Ya se usaron los 7 días extra" : "Prorrogar la vigencia 1–7 días (envía imagen + mensaje al cliente)"}
                          >
                            <CalendarPlus className="w-3 h-3" /> Prorrogar ({Number(activePromo.extended_days_total || 0)}/{MAX_EXTRA_DAYS})
                          </button>
                        )}
                        {expiredPromo && !isPaid && (
                          <button
                            data-testid={`promo-reactivate-${p.id}`}
                            disabled={Number(expiredPromo.extended_days_total || 0) >= MAX_EXTRA_DAYS}
                            onClick={() => setExtendTarget({ plan: p, promo: expiredPromo })}
                            className="text-[10px] px-2.5 py-1 rounded border border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 flex items-center gap-1 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            title="La promo venció hace menos de 3 días: puedes prorrogarla y se reactiva desde ahora"
                          >
                            <RotateCcw className="w-3 h-3" /> Reactivar / prorrogar ({Number(expiredPromo.extended_days_total || 0)}/{MAX_EXTRA_DAYS})
                          </button>
                        )}
                        {activePromo && !isPaid && (
                          <button
                            data-testid={`promo-liquidate-${p.id}`}
                            disabled={liquidatingId === p.id}
                            onClick={() => liquidateWithPromo(p.id, activePromo.id)}
                            className="text-[10px] px-2.5 py-1 rounded border border-amber-500/70 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 flex items-center gap-1 font-semibold disabled:opacity-50 disabled:cursor-wait"
                            title="Liquidar aplicando la promoción — el sistema genera automáticamente el abono restante con descuento"
                          >
                            {liquidatingId === p.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <CheckCircle2 className="w-3 h-3" />}
                            Liquidar con promo · {fmtMoney(activePromo.new_remaining, cur)}
                          </button>
                        )}
                        {isPaid && (
                          <span className="text-[10px] text-neutral-500 italic">
                            Este trabajo ya está liquidado — no se pueden aplicar más descuentos.
                          </span>
                        )}
                      </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Temp promo modal (rendered from this section only) */}
      {promoPlan && (
        <TempPromoModal
          plan={promoPlan}
          clientUser={selectedUser}
          authHeader={authHeader}
          onClose={async () => { setPromoPlan(null); await refreshAll?.(); }}
        />
      )}
      {extendTarget && (
        <PromoExtendModal
          plan={extendTarget.plan}
          promo={extendTarget.promo}
          clientUser={selectedUser}
          authHeader={authHeader}
          onClose={async () => { setExtendTarget(null); await refreshAll?.(); }}
        />
      )}
      <PromoLiquidateDialog
        target={liquidateTarget}
        onClose={() => setLiquidateTarget(null)}
        onConfirm={confirmLiquidate}
      />
    </motion.div>
  );
};

export default PromotionsSection;
